'use client';
import React, { useEffect, useRef } from 'react';

export default function CtaSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="cta" ref={sectionRef} className="py-28 md:py-40 relative z-10 bg-background border-t border-primary/8 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-accent/8 rounded-full blur-3xl" />
      </div>
      <div className="container mx-auto px-6 max-w-4xl relative z-10 text-center">
        <div className="animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
          <span className="text-[10px] uppercase tracking-ultra text-accent font-medium block mb-6">
            Rejoignez les propriétaires Anse des Rochers
          </span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-tight text-foreground mb-8">
            Préparez dès aujourd'hui{' '}
            <span className="italic text-teal-gradient">l'accueil de demain.</span>
          </h2>
          <p className="text-sm text-foreground-muted leading-relaxed mb-10 max-w-xl mx-auto">
            Activez votre livret numérique gratuitement et offrez à vos voyageurs
            une expérience d'accueil à la hauteur de la beauté de la Guadeloupe.
          </p>

          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-12">
            {[
              'Activation en quelques minutes',
              'Aucune carte bancaire requise',
              'Support inclus, sans frais cachés',
              'Partenariat officiel Anse des Rochers',
            ]?.map((item) => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-5 h-5 bg-primary/10 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <span className="text-sm text-foreground-muted">{item}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <a
            href="https://livretvacancesguadeloupe.fr/connexion"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white px-12 py-5 text-[11px] uppercase tracking-wide-xl font-medium transition-all duration-400 overflow-hidden"
          >
            <span className="relative z-10">Activer mon livret gratuit</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="relative z-10 group-hover:translate-x-1 transition-transform duration-300">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>

          {/* Partnership badge */}
          <div className="mt-12 pt-10 border-t border-primary/10">
            <p className="text-[10px] uppercase tracking-ultra text-foreground-subtle mb-2">
              Partenariat officiel
            </p>
            <p className="text-sm font-medium text-foreground-muted">
              Livret Vacances Guadeloupe × Anse des Rochers
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}